import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SubscriptionStatus from "@/components/SubscriptionStatus";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Lock, Upload, AlertTriangle, CheckCircle2, Clock, Hourglass, CreditCard, Apple, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PaymentMethodsPicker from "@/components/PaymentMethodsPicker";
import { isNativeIOS, isNativeApp } from "@/lib/platform";
import IAP from "@/lib/iap";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

const STYLE = {
  limited_access_active: {
    color: "bg-amber-50 border-amber-300 dark:bg-amber-50 dark:border-amber-300",
    text: "text-amber-900",
    accent: "text-amber-700",
    icon: Clock,
  },
  limited_access_expired: {
    color: "bg-rose-50 border-rose-300 dark:bg-rose-50 dark:border-rose-300",
    text: "text-rose-900",
    accent: "text-rose-700",
    icon: Lock,
  },
  pending_payment_verification: {
    color: "bg-blue-50 border-blue-300 dark:bg-blue-50 dark:border-blue-300",
    text: "text-blue-900",
    accent: "text-blue-700",
    icon: Hourglass,
  },
  payment_rejected: {
    color: "bg-rose-50 border-rose-300 dark:bg-rose-50 dark:border-rose-300",
    text: "text-rose-900",
    accent: "text-rose-700",
    icon: AlertTriangle,
  },
  full_access: {
    color: "bg-emerald-50 border-emerald-300",
    text: "text-emerald-900",
    accent: "text-emerald-700",
    icon: CheckCircle2,
  },
};

/** Renders the student's access banner. Self-fetches
 *  `/student/access-status/per-course` and renders ONE row per enrolled
 *  course that isn't at full-access — each with its own course name and
 *  Subscribe / Pay button so students with multiple courses know exactly
 *  which one every button unlocks.
 *
 *  Pass an optional `onChange` callback to react to state refreshes. */
export default function AccessBanner({ compact = false, onChange }) {
  const [states, setStates] = useState(null); // array of per-course access states
  const [paySettings, setPaySettings] = useState(null);
  const [systems, setSystems] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const nav = useNavigate();

  const reload = useCallback(async () => {
    try {
      const [perCourse, p, sys, pc] = await Promise.all([
        api.get("/student/access-status/per-course").then((r) => r.data),
        api.get("/payment-settings/public").then((r) => r.data).catch(() => null),
        api.get("/payment-systems/public").then((r) => r.data).catch(() => []),
        api.get("/student/profile/completion-status").then((r) => r.data).catch(() => null),
      ]);
      setStates(Array.isArray(perCourse) ? perCourse : []);
      setPaySettings(p);
      setSystems(sys || []);
      setProfileCompletion(pc);
      // Backwards-compat: onChange receives the FIRST non-full course state
      // (or the first entry) so pages that watch it (StudentDashboard) still work.
      const primary = perCourse.find((s) => !s.is_full) || perCourse[0] || null;
      onChange?.(primary);
    } catch { /* noop */ }
  }, [onChange]);

  useEffect(() => { reload(); }, [reload]);

  if (!states) return null;
  // Only show banners for courses that require attention (not full access).
  // In compact mode we also drop courses at full access entirely.
  const rowsToShow = states.filter((s) => s.has_enrolled_course && (!s.is_full || !compact));
  if (rowsToShow.length === 0) return null;

  // PayMongo is only "active" when BOTH enabled by admin AND the link is set.
  const paymongoActive = !!(paySettings?.paymongo_enabled && paySettings?.paymongo_payment_link);
  const hasManualSystems = (systems || []).length > 0;

  const openPicker = () => {
    if (profileCompletion && !profileCompletion.is_complete) {
      toast.info("Please complete your profile before proceeding with payment.");
      nav("/student/profile/complete?next=/student/enrollments");
      return;
    }
    setPickerOpen(true);
  };

  const openReceipt = () => {
    if (profileCompletion && !profileCompletion.is_complete) {
      toast.info("Please complete your profile before uploading a receipt.");
      nav("/student/profile/complete?next=/student/enrollments");
      return;
    }
    setReceiptOpen(true);
  };

  return (
    <>
      <div className="space-y-3">
        {rowsToShow.map((state) => (
          <CourseAccessRow
            key={state.course_id || "primary"}
            state={state}
            paymongoActive={paymongoActive}
            hasManualSystems={hasManualSystems}
            onOpenPicker={openPicker}
            onOpenReceipt={openReceipt}
            reload={reload}
          />
        ))}
      </div>

      <PaymentMethodsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onUploadReceipt={() => setReceiptOpen(true)}
      />

      <UploadReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        onSubmitted={reload}
        instructions={paySettings?.payment_instructions}
      />
    </>
  );
}


/** Renders the banner card for ONE course. Handles all access states
 *  (limited / expired / pending_review / rejected / full) and platform
 *  differences (iOS uses IAP, web/Android uses PayMongo + Upload Receipt). */
function CourseAccessRow({ state, paymongoActive, hasManualSystems, onOpenPicker, onOpenReceipt, reload }) {
  const style = STYLE[state.status] || STYLE.limited_access_active;
  const Icon = style.icon;
  const showPayBtn = !state.is_full && !state.is_pending_review;
  const showUploadBtn = !state.is_full;

  return (
    <Card className={`p-4 sm:p-5 border-2 overflow-hidden ${style.color}`} data-testid={`access-banner-${state.course_id}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${style.accent} bg-white/60`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          {/* Course name header — makes it unambiguous which course each Subscribe button unlocks. */}
          {state.course_name && (
            <div className={`text-[11px] uppercase tracking-wide font-semibold ${style.accent} mb-0.5`} data-testid={`banner-course-name-${state.course_id}`}>
              {state.course_name}
            </div>
          )}
          <BannerCopy state={state} style={style} />
          {!state.is_full && !state.is_pending_review && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {state.is_limited && (
                <>
                  <Badge variant="outline" className="bg-white/60 border-amber-300 text-amber-900">
                    <Clock className="w-3 h-3 mr-1" /> {state.days_remaining} day{state.days_remaining === 1 ? "" : "s"} remaining
                  </Badge>
                  {state.question_limit > 0 && (
                    <Badge variant="outline" className="bg-white/60 border-amber-300 text-amber-900">
                      {state.questions_remaining} of {state.question_limit} question{state.question_limit === 1 ? "" : "s"} left
                    </Badge>
                  )}
                </>
              )}
              {state.payment.rejection_reason && (
                <span className="text-xs text-rose-700">Reason: {state.payment.rejection_reason}</span>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {state.is_full ? (
              <SubscriptionStatus state={state} />
            ) : isNativeIOS() ? (
              <IosIapCtas state={state} onDone={reload} />
            ) : (
              <>
                {showPayBtn && (paymongoActive || hasManualSystems) && (
                  <Button onClick={onOpenPicker} className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-normal break-words text-left h-auto py-2 max-w-full"
                    size="sm" data-testid={`banner-view-methods-btn-${state.course_id}`}>
                    <CreditCard className="w-4 h-4 mr-1.5 shrink-0" />
                    <span className="min-w-0">
                      {state.pricing?.price_display
                        ? `Pay ${state.pricing.price_display}`
                        : "View Payment Methods"}
                    </span>
                  </Button>
                )}
                {showUploadBtn && (
                  <Button onClick={onOpenReceipt} variant="outline"
                    className={`${state.is_pending_review ? "border-blue-300 text-blue-800" : "border-amber-300 text-amber-800"}`}
                    size="sm" data-testid={`banner-upload-receipt-btn-${state.course_id}`}>
                    <Upload className="w-4 h-4 mr-1.5" /> Upload Receipt
                  </Button>
                )}
                {showPayBtn && !paymongoActive && !hasManualSystems && (
                  <span className="text-xs text-amber-800 self-center">
                    No payment methods enabled yet — please contact your administrator.
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function BannerCopy({ state, style }) {
  const t = style.text;
  if (state.is_full) return (
    <div>
      <div className={`font-semibold ${t}`}>
        {state.full_access_source === "subscription" ? "You're subscribed — full access active." : "Full access active."}
      </div>
      <div className="text-xs text-emerald-700 mt-0.5">You can use every feature of {state.course_name || "your course"}.</div>
    </div>
  );
  if (state.is_pending_review) return (
    <div>
      <div className={`font-semibold ${t}`}>Your payment proof has been submitted.</div>
      <div className={`text-xs ${t} mt-0.5`}>It is waiting for admin verification. You will be notified once approved.</div>
    </div>
  );
  if (state.is_expired) return (
    <div>
      <div className={`font-semibold ${t}`}>Your limited access has expired.</div>
      <div className={`text-xs ${t} mt-0.5`}>Please complete your payment to continue accessing the course.</div>
    </div>
  );
  if (state.status === "payment_rejected") return (
    <div>
      <div className={`font-semibold ${t}`}>Your payment receipt was rejected.</div>
      <div className={`text-xs ${t} mt-0.5`}>Please upload a valid receipt to continue.</div>
    </div>
  );
  // Active limited access
  const usedCount = state.questions_used ?? 0;
  const limit = state.question_limit ?? 0;
  const remaining = state.questions_remaining ?? 0;
  return (
    <div>
      <div className={`font-semibold ${t}`}>You are using temporary access.</div>
      {limit > 0 && (
        <div className={`text-xs ${t} mt-0.5`} data-testid="banner-quota-line">
          Remaining questions: <span className="font-semibold">{remaining}</span> of <span className="font-semibold">{limit}</span>
          {usedCount > 0 ? ` (${usedCount} used)` : ""}.
        </div>
      )}
      <div className={`text-xs ${t} mt-0.5`}>Complete your payment to unlock the full course.</div>
    </div>
  );
}

function UploadReceiptDialog({ open, onOpenChange, onSubmitted, instructions }) {
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = (f) => {
    if (!f) return null;
    if (!ALLOWED_MIMES.includes(f.type)) { toast.error("Only JPG, PNG, or PDF files are allowed"); return null; }
    if (f.size > MAX_BYTES) { toast.error("File must be 5 MB or smaller"); return null; }
    return f;
  };

  const submit = async () => {
    if (!ref.trim() && !file) { toast.error("Provide a reference number or upload proof of payment"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payment_reference_number", ref.trim().slice(0, 80));
      fd.append("notes", notes.trim().slice(0, 600));
      if (file) fd.append("proof_of_payment", file);
      await api.post("/student/payments/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt submitted — awaiting admin verification");
      onOpenChange(false);
      setRef(""); setFile(null); setNotes("");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit receipt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid="upload-receipt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" /> Upload Payment Receipt</DialogTitle>
          <DialogDescription>Submit your payment receipt to upgrade to full course access.</DialogDescription>
        </DialogHeader>
        {instructions && (<div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900 whitespace-pre-line">{instructions}</div>)}
        <div>
          <Label htmlFor="ar_ref">Payment Reference Number</Label>
          <Input id="ar_ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g., 4231 0987 2233" className="font-mono mt-1.5" data-testid="receipt-ref-input" />
        </div>
        <div>
          <Label htmlFor="ar_proof">Receipt File</Label>
          <Input id="ar_proof" type="file" accept="image/jpeg,image/png,application/pdf"
            onChange={(e) => setFile(validate(e.target.files?.[0] || null))}
            className="mt-1.5 cursor-pointer file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
            data-testid="receipt-file-input" />
          <p className="text-[11px] text-slate-500 mt-1">JPG, PNG, or PDF (max 5MB).</p>
          {file && (<div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><Upload className="w-3 h-3" /> {file.name}</div>)}
        </div>
        <div>
          <Label htmlFor="ar_notes">Notes <span className="text-slate-400 text-xs ml-1">optional</span></Label>
          <Textarea id="ar_notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="receipt-notes-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className="w-full sm:w-auto">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto" data-testid="receipt-submit-btn">
            {busy ? "Submitting…" : "Submit Payment Proof"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * IosIapCtas — the Apple-compliant purchase strip shown to native iOS
 * users. Renders "Subscribe via App Store" + "Restore Purchases".
 * Product is registered globally by IAP.init() (called from
 * CapacitorBootstrap after login). Reads the live localized price from
 * the store so nothing is hard-coded.
 */
function IosIapCtas({ state, onDone }) {
  const [product, setProduct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // state.pricing is now provided by /student/access-status/per-course so
  // we no longer need to refetch /courses.
  const tier = state?.pricing || null;

  // Load the App Store product for the tier so we can show the localised price.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await IAP.init();
        if (!tier?.product_id) return;
        await IAP.ensureProducts([tier.product_id]);
        for (let i = 0; i < 20 && !cancelled; i++) {
          const p = IAP.getProduct(tier.product_id);
          if (p) { setProduct(p); break; }
          await new Promise((r) => setTimeout(r, 500));
        }
      } catch (e) {
        console.warn("[IAP] init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [tier?.product_id]);

  const purchase = async () => {
    if (!state?.course_id) {
      toast.error("Select a course before subscribing.");
      return;
    }
    if (!tier?.product_id) {
      toast.error("This course has no price tier configured yet. Please contact your administrator.");
      return;
    }
    setBusy(true);
    try {
      await IAP.buy({ courseId: state.course_id, productId: tier.product_id });
      toast.success("Purchase complete — full access unlocked!");
      onDone?.();
    } catch (e) {
      toast.error(e?.message || "Could not start the Apple purchase.");
    } finally { setBusy(false); }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      await IAP.restore({ courseId: state?.course_id });
      toast.success("Purchase restored — refreshing your access.");
      onDone?.();
    } catch (e) {
      toast.error(e?.message || "Could not restore purchases.");
    } finally { setRestoring(false); }
  };

  // Prefer the store's localised price. Course name is already shown in the
  // banner card header, so no need to repeat it in the button label.
  const priceLabel = product?.price || tier?.price_display;

  return (
    <>
      <Button
        onClick={purchase}
        disabled={busy || restoring || !tier?.product_id}
        className="bg-slate-900 hover:bg-slate-800 text-white whitespace-normal break-words text-left h-auto py-2 max-w-full"
        size="sm"
        data-testid="banner-iap-subscribe-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin shrink-0" /> : <Apple className="w-4 h-4 mr-1.5 shrink-0" />}
        <span className="min-w-0">
          {busy ? "Starting…" : (priceLabel ? `Subscribe — ${priceLabel}` : "Subscribe")}
        </span>
      </Button>
      <Button
        onClick={restore}
        disabled={busy || restoring}
        variant="outline"
        size="sm"
        className="border-slate-300 text-slate-800"
        data-testid="banner-iap-restore-btn"
      >
        {restoring ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
        {restoring ? "Restoring…" : "Restore Purchases"}
      </Button>
      {!product && (
        <span className="text-[11px] text-amber-800 self-center">
          Loading App Store pricing…
        </span>
      )}
    </>
  );
}
